import React from "react";
import { ChevronDown, GripVertical, Plus, Trash2 } from "lucide-react";
import { Input } from "src/components/ui/Input";
import { slab, faqLabelClass } from "./faqConstants";
import type { FaqSortableSectionRenderProps } from "./FaqSortableSection";
import { FaqSortableRowList } from "./FaqSortableRowList";
import { useFaqStore } from "src/store/faqStore";

type FaqSectionCardProps = {
  sectionIndex: number;
  /** Present when managers reorder categories via @dnd-kit (handle-driven). */
  sectionSortable?: FaqSortableSectionRenderProps;
};

export const FaqSectionCard: React.FC<FaqSectionCardProps> = ({
  sectionIndex,
  sectionSortable,
}) => {
  const section = useFaqStore((s) => s.form?.faq[sectionIndex]);
  const canManage = useFaqStore((s) => s.canManage);
  const disabled = !canManage;
  const open = useFaqStore((s) => s.expandedSection === sectionIndex);
  const toggleSection = useFaqStore((s) => s.toggleSection);
  const patchSection = useFaqStore((s) => s.patchSection);
  const removeSection = useFaqStore((s) => s.removeSection);
  const addContentRow = useFaqStore((s) => s.addContentRow);

  if (!section) return null;

  return (
    <li
      ref={sectionSortable?.setNodeRef}
      style={sectionSortable?.style}
      className={sectionSortable?.isDragging ? "opacity-70" : "opacity-100"}
    >
      <div
        className={`overflow-hidden rounded-[1.75rem] border-4 bg-white shadow-[0_8px_0_#D0D0D0] transition-colors border-[#E5E5E5]`}
      >
        <div className="flex flex-wrap items-start gap-2 border-b-4 border-[#E5E5E5] bg-[#FAFAFA] p-4 sm:p-5">
          {canManage && (
            <div
              className="mt-1 flex h-10 w-6 cursor-grab touch-none items-center justify-center text-[#AFAFAF] hover:text-[#777777] active:cursor-grabbing"
              aria-hidden
              {...(sectionSortable?.dragHandleProps ?? {})}
            >
              <GripVertical className="h-5 w-5" />
            </div>
          )}

          <button
            type="button"
            onClick={() => toggleSection(sectionIndex)}
            aria-expanded={open}
            className="flex min-h-[44px] min-w-0 flex-1 items-start gap-3 text-left"
          >
            <span
              className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg font-extrabold text-white shadow-[0_4px_0_#4F46E5]"
              style={{ backgroundColor: slab.indigo }}
            >
              {sectionIndex + 1}
            </span>
            <span className="min-w-0">
              <span className="block text-lg font-extrabold leading-snug text-[#3C3C3C] sm:text-xl">
                {section.title.trim() || "Untitled category"}
              </span>
              <span className="mt-1 block text-sm font-semibold text-[#777777]">
                {section.content.length} entr
                {section.content.length === 1 ? "y" : "ies"}
              </span>
            </span>
            <ChevronDown
              className={`ml-auto mt-1 h-6 w-6 shrink-0 text-[#777777] transition-transform ${open ? "rotate-180" : ""}`}
              aria-hidden
            />
          </button>

          {canManage && (
            <div className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto sm:justify-end">
              <button
                type="button"
                onClick={() => removeSection(sectionIndex)}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border-2 border-[#FECACA] bg-[#FEF2F2] px-3 text-sm font-extrabold text-[#B91C1C] shadow-[0_4px_0_#FCA5A5] active:translate-y-0.5"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Remove
              </button>
            </div>
          )}
        </div>

        {open && (
          <div className="space-y-6 p-5 sm:p-8">
            <label className={faqLabelClass}>
              Category title
              <Input
                accent="teal"
                type="text"
                disabled={disabled}
                value={section.title}
                onChange={(e) =>
                  patchSection(sectionIndex, { title: e.target.value })
                }
                placeholder="e.g. Vue dot colors"
              />
            </label>

            <div className="my-2 h-px bg-[#E5E5E5]" />

            <div>
              <h3 className="text-lg font-extrabold text-[#3C3C3C]">
                Questions &amp; answers
              </h3>
              <p className="mt-1 text-sm font-semibold text-[#777777]">
                Each row is one <code className="font-mono">subTitle</code> +{" "}
                <code className="font-mono">description</code> pair.
                {canManage && (
                  <span className="ml-1 text-[#AFAFAF]">
                    Drag <GripVertical className="inline h-3 w-3" /> to reorder
                    rows.
                  </span>
                )}
              </p>

              <FaqSortableRowList sectionIndex={sectionIndex} />

              {canManage && (
                <button
                  type="button"
                  onClick={() => addContentRow(sectionIndex)}
                  className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#6366F1]/50 bg-[#EEF2FF] px-4 text-sm font-extrabold text-[#4338CA] shadow-[0_4px_0_#A5B4FC]/50 transition-transform active:translate-y-0.5 sm:w-auto"
                >
                  <Plus className="h-5 w-5" aria-hidden />
                  Add entry in this category
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </li>
  );
};
